/**
 * LoweredEmitter — walks Expression/Statement/LoweredNode trees and emits pseudo-C.
 *
 * No SSA knowledge. No defMap. No expression cache.
 * Just a tree walker with operator precedence and formatting.
 */

import { Expression, Statement, LoweredNode } from './ExpressionIR';

/** Maps WASM value types to C-style type names for display. */
export const WASM_TO_C_TYPE: Record<string, string> = {
  'i32': 'int', 'i64': 'long', 'f32': 'float', 'f64': 'double', 'v128': 'v128',
  'funcref': 'funcref', 'externref': 'externref',
};

const PRECEDENCE: Record<string, number> = {
  '?:': 1, '||': 2, '&&': 3, '|': 4, '^': 5, '&': 6,
  '==': 7, '!=': 7, '<': 8, '>': 8, '<=': 8, '>=': 8, '<u': 8, '>u': 8, '<=u': 8, '>=u': 8,
  '<<': 9, '>>': 9, '>>>': 9, '+': 10, '-': 10,
  '*': 11, '/': 11, '%': 11, '/u': 11, '%u': 11,
  'unary': 12,
};

// Precedence sentinel meaning "always parenthesize unless the operand is atomic".
const FORCE_PARENS = 100;

function cType(wasmType: string): string {
  return WASM_TO_C_TYPE[wasmType] || wasmType;
}

const LOAD_TYPE_CAST: Record<string, string> = {
  'i32.load8_s': 'byte',
  'i32.load8_u': 'ubyte',
  'i32.load16_s': 'short',
  'i32.load16_u': 'ushort',
  'i64.load8_s': 'byte',
  'i64.load8_u': 'ubyte',
  'i64.load16_s': 'short',
  'i64.load16_u': 'ushort',
  'i64.load32_s': 'int',
  'i64.load32_u': 'uint',
};

const UNSIGNED_OPS: Record<string, string> = {
  '<u': '<', '>u': '>', '<=u': '<=', '>=u': '>=', '/u': '/', '%u': '%',
};

// The C scalar type a memory access reads or writes, used to cast a frame-buffer slot
// pointer (`*(int*)&frame[k]`). Derived from the load/store type's width and signedness.
function accessCType(memoryType: string): string {
  if (memoryType.includes('8')) {
    return memoryType.includes('_u') ? 'unsigned char' : 'char';
  }
  if (memoryType.includes('16')) {
    return memoryType.includes('_u') ? 'unsigned short' : 'short';
  }
  if (memoryType.startsWith('i64')) {
    return memoryType.includes('32') ? (memoryType.includes('_u') ? 'unsigned int' : 'int') : 'long';
  }
  if (memoryType.startsWith('f32')) {
    return 'float';
  }
  if (memoryType.startsWith('f64')) {
    return 'double';
  }
  return 'int';
}

// WASM binary operators with no C infix equivalent — emitted as function calls.
const CALL_STYLE_BINARY_OPS = new Set(['rotl', 'rotr', 'min', 'max', 'copysign']);

/** Formats a WASM constant as a C literal, honoring float/special values. */
function formatConstant(value: number | bigint, type: string): string {
  if (typeof value === 'bigint') {
    return String(value);
  }
  if (type === 'f32' || type === 'f64') {
    if (Number.isNaN(value)) {
      return 'NAN';
    }
    if (value === Infinity) {
      return 'INFINITY';
    }
    if (value === -Infinity) {
      return '-INFINITY';
    }
    const text = String(value);
    if (!/[.eExXnN]/.test(text)) {
      return `${text}.0`;
    }
    return text;
  }
  return String(value);
}

function isIntrinsicOp(op: string): boolean {
  return op.includes('x4_') || op.includes('x8_') || op.includes('x16_') || op.includes('x2_') ||
         op.startsWith('v128_') || op.includes('atomic_') ||
         op.startsWith('struct_') || op.startsWith('array_') ||
         op.startsWith('table_') || op.startsWith('memory_') ||
         op.startsWith('ref_') || op.startsWith('i31_') ||
         op.startsWith('extern_') || op.startsWith('any_');
}

function maybeWrap(text: string, exprPrec: number, parentPrec: number): string {
  return exprPrec < parentPrec ? `(${text})` : text;
}

function memoryIndex(address: string, offset: number): string {
  if (offset <= 0) {
    return address;
  }
  return address === '0' ? String(offset) : `${address} + ${offset}`;
}

export interface FieldResolver {
  resolveField(baseName: string, offset: number): string | null;
}

export interface FrameBufferDeclaration {
  name: string;
  size: number;
}

export function emitLowered(
  node: LoweredNode,
  funcSignature: string,
  paramNames: Set<string>,
  fieldResolver?: FieldResolver,
  frameBuffer?: FrameBufferDeclaration | null,
): string {
  return new LoweredCodeWriter(paramNames, fieldResolver, frameBuffer).write(node, funcSignature);
}

class LoweredCodeWriter {
  private readonly lines: string[] = [];
  private indentLevel = 0;
  // Non-parameter locals that are read but never assigned. WASM zero-initializes locals,
  // so each such read is the value 0 (its SSA entry version has no definition); rendering
  // them as 0 avoids emitting an undefined variable.
  private readonly zeroInitLocals = new Set<string>();

  constructor(
    private readonly paramNames: Set<string>,
    private readonly fieldResolver?: FieldResolver,
    private readonly frameBuffer?: FrameBufferDeclaration | null,
  ) {}

  write(node: LoweredNode, funcSignature: string): string {
    this.collectZeroInitLocals(node);
    this.emit(`${funcSignature} {`);
    this.indentLevel++;
    this.emitDeclarations(node);
    this.emitNode(node);
    this.trimTrailingVoidReturn(funcSignature);
    this.indentLevel--;
    this.emit('}');
    return this.lines.join('\n');
  }

  // A local read with no assignment anywhere (and not a parameter or the frame buffer) is
  // an uninitialized WASM local, whose value is 0. Self-assign copies have already been
  // eliminated, so what remains is a genuine read of the never-written entry version.
  private collectZeroInitLocals(node: LoweredNode): void {
    const assigned = new Set<string>();
    collectAssignTargets(node, assigned);
    const referenced = new Set<string>();
    collectReferencedVars(node, referenced);
    for (const name of referenced) {
      if (!assigned.has(name) && !this.paramNames.has(name) && name !== this.frameBuffer?.name) {
        this.zeroInitLocals.add(name);
      }
    }
  }

  private emit(text: string): void {
    this.lines.push('  '.repeat(this.indentLevel) + text);
  }

  private emitDeclarations(node: LoweredNode): void {
    const declaredVars = new Set<string>();
    collectAssignTargets(node, declaredVars);
    const referencedVars = new Set<string>();
    collectReferencedVars(node, referencedVars);
    // Referenced-but-not-assigned variables are declared too, except uninitialized locals
    // (rendered as the constant 0, so they need no declaration).
    for (const referencedVar of referencedVars) {
      if (!this.zeroInitLocals.has(referencedVar)) {
        declaredVars.add(referencedVar);
      }
    }
    const varTypes = new Map<string, string>();
    collectAssignTypes(node, varTypes);
    let emittedDeclaration = false;
    // The stack-frame buffer, when the base escaped (frame-slot rewrite). Its name no
    // longer appears as a plain variable, so it is declared explicitly as a byte array.
    if (this.frameBuffer) {
      this.emit(`unsigned char ${this.frameBuffer.name}[${this.frameBuffer.size}];`);
      emittedDeclaration = true;
    }
    for (const varName of declaredVars) {
      if (!this.paramNames.has(varName)) {
        this.emit(`${cType(varTypes.get(varName) || 'i32')} ${varName};`);
        emittedDeclaration = true;
      }
    }
    if (emittedDeclaration) {
      this.emit('');
    }
  }

  private trimTrailingVoidReturn(funcSignature: string): void {
    if (funcSignature.startsWith('void ') && this.lines.length > 0 &&
        this.lines[this.lines.length - 1].trim() === 'return;') {
      this.lines.pop();
    }
  }

  private emitNode(node: LoweredNode): void {
    switch (node.kind) {
      case 'sequence': this.emitSequence(node); break;
      case 'block': this.emitBlock(node); break;
      case 'if': this.emitIf(node); break;
      case 'while': this.emitBlockNode(node.condition ? `while (${this.formatExpression(node.condition, 0)}) {` : 'while (true) {', node.body); break;
      case 'do_while': this.emitDoWhile(node); break;
      case 'for': this.emitBlockNode(`for (${this.formatStatementInline(node.init)}; ${this.formatExpression(node.condition, 0)}; ${this.formatStatementInline(node.increment)}) {`, node.body); break;
      case 'switch': this.emitSwitch(node); break;
      case 'break': this.emit('break;'); break;
      case 'continue': this.emit('continue;'); break;
      case 'return': this.emit(this.formatReturn(node.values)); break;
      case 'unreachable': this.emit('unreachable();'); break;
      case 'labeled_block': this.emitBlockNode(`${node.label}: {`, node.body); break;
      case 'labeled_break': this.emit(`break ${node.label};`); break;
      case 'labeled_continue': this.emit(`continue ${node.label};`); break;
      case 'label': this.emit(`${node.name}:`); break;
      case 'goto': this.emit(`goto ${node.target};`); break;
    }
  }

  private emitSequence(node: LoweredNode & { kind: 'sequence' }): void {
    for (let index = 0; index < node.children.length; index++) {
      const child = node.children[index];
      this.emitNode(child);
      // Code after a terminator is dead — unless the next sibling is a label, which is a
      // goto target and therefore reachable (goto-style fallback for unstructured CFGs).
      const next = node.children[index + 1];
      if (alwaysTerminates(child) && (!next || next.kind !== 'label')) {
        break;
      }
    }
  }

  private emitBlock(node: LoweredNode & { kind: 'block' }): void {
    for (const statement of node.body) {
      this.emitStatement(statement);
      if (statement.kind === 'return' || statement.kind === 'unreachable') {
        break;
      }
    }
  }

  private emitBlockNode(header: string, body: LoweredNode): void {
    this.emit(header);
    this.indentLevel++;
    this.emitNode(body);
    this.indentLevel--;
    this.emit('}');
  }

  private emitDoWhile(node: LoweredNode & { kind: 'do_while' }): void {
    this.emit('do {');
    this.indentLevel++;
    this.emitNode(node.body);
    this.indentLevel--;
    this.emit(`} while (${this.formatExpression(node.condition, 0)});`);
  }

  private emitIf(node: LoweredNode & { kind: 'if' }): void {
    const hasThen = !isEmptyLowered(node.thenBody);
    const hasElse = node.elseBody !== null && !isEmptyLowered(node.elseBody);
    if (!hasThen && !hasElse) {
      return;
    }

    this.emit(`if (${this.formatExpression(node.condition, 0)}) {`);
    this.indentLevel++;
    this.emitNode(node.thenBody);
    this.indentLevel--;

    if (!hasElse) {
      this.emit('}');
      return;
    }
    const elseBody = node.elseBody!;
    if (elseBody.kind === 'if') {
      this.emitElseIf(elseBody);
    } else {
      this.emit('} else {');
      this.indentLevel++;
      this.emitNode(elseBody);
      this.indentLevel--;
      this.emit('}');
    }
  }

  private emitElseIf(node: LoweredNode & { kind: 'if' }): void {
    this.emit(`} else if (${this.formatExpression(node.condition, 0)}) {`);
    this.indentLevel++;
    this.emitNode(node.thenBody);
    this.indentLevel--;
    if (node.elseBody && !isEmptyLowered(node.elseBody)) {
      this.emit('} else {');
      this.indentLevel++;
      this.emitNode(node.elseBody);
      this.indentLevel--;
    }
    this.emit('}');
  }

  private emitSwitch(node: LoweredNode & { kind: 'switch' }): void {
    this.emit(`switch (${this.formatExpression(node.selector, 0)}) {`);
    this.indentLevel++;
    for (const caseEntry of node.cases) {
      for (const caseValue of caseEntry.values) {
        this.emit(`case ${caseValue}:`);
      }
      this.indentLevel++;
      this.emitNode(caseEntry.body);
      if (!alwaysTerminates(caseEntry.body)) {
        this.emit('break;');
      }
      this.indentLevel--;
    }
    if (!isEmptyLowered(node.defaultBody)) {
      this.emit('default:');
      this.indentLevel++;
      this.emitNode(node.defaultBody);
      this.indentLevel--;
    }
    this.indentLevel--;
    this.emit('}');
  }

  private emitStatement(statement: Statement): void {
    switch (statement.kind) {
      case 'assign':
        this.emit(`${statement.target} = ${this.formatExpression(statement.value, 0)};`);
        break;
      case 'store':
        this.emitStore(statement);
        break;
      case 'call':
        this.emit(this.formatCallStatement(statement.result, statement.name, statement.args));
        break;
      case 'call_indirect':
        this.emit(this.formatCallStatement(statement.result, `table[${this.formatExpression(statement.tableIndex, 0)}]`, statement.args));
        break;
      case 'global_set':
        this.emit(`${statement.name} = ${this.formatExpression(statement.value, 0)};`);
        break;
      case 'return':
        this.emit(this.formatReturn(statement.values));
        break;
      case 'unreachable':
        this.emit('unreachable();');
        break;
      case 'expr':
        this.emit(`${this.formatExpression(statement.value, 0)};`);
        break;
      default:
        this.emit(`/* unknown stmt: ${(statement as Statement).kind} */`);
        break;
    }
  }

  private emitStore(statement: Statement & { kind: 'store' }): void {
    if (statement.address.kind === 'frame_slot') {
      const slot = this.formatFrameSlot(statement.address);
      const value = this.formatExpression(statement.value, 0);
      if (statement.storeType.includes('v128') || statement.storeType.includes('atomic')) {
        this.emit(`${statement.storeType.replace(/\./g, '_')}(${slot}, ${value});`);
      } else {
        this.emit(`*(${accessCType(statement.storeType)}*)${slot} = ${value};`);
      }
      return;
    }
    if (statement.address.kind === 'field_access') {
      this.emit(`${this.formatFieldAccess(statement.address.base, statement.address.offset)} = ${this.formatExpression(statement.value, 0)};`);
      return;
    }
    const plain = statement.storeType.includes('v128') || statement.storeType.includes('atomic');
    if (!plain) {
      const namedField = this.namedFieldAccess(statement.address, statement.offset);
      if (namedField) {
        this.emit(`${namedField} = ${this.formatExpression(statement.value, 0)};`);
        return;
      }
    }
    const target = memoryIndex(this.formatExpression(statement.address, 0), statement.offset);
    if (statement.storeType.includes('v128') || statement.storeType.includes('atomic')) {
      this.emit(`${statement.storeType.replace(/\./g, '_')}(${target}, ${this.formatExpression(statement.value, 0)});`);
    } else {
      this.emit(`memory[${target}] = ${this.formatExpression(statement.value, 0)};`);
    }
  }

  private formatCallStatement(result: string | null, callee: string, args: Expression[]): string {
    const call = `${callee}(${args.map(arg => this.formatExpression(arg, 0)).join(', ')})`;
    return result ? `${result} = ${call};` : `${call};`;
  }

  private formatFieldAccess(base: Expression, offset: number): string {
    const baseText = this.formatExpression(base, FORCE_PARENS);
    if (this.fieldResolver && base.kind === 'var') {
      const fieldName = this.fieldResolver.resolveField(base.name, offset);
      if (fieldName) {
        return `${baseText}->${fieldName}`;
      }
    }
    return `${baseText}[${offset}]`;
  }

  // Renders `base->field` for a plain base+offset access only when DWARF names that field.
  // The struct-access pass skips offset 0, so the first field reaches here as a plain load
  // or store; returning null otherwise leaves the memory[...] form untouched.
  private namedFieldAccess(address: Expression, offset: number): string | null {
    if (this.fieldResolver && address.kind === 'var') {
      const fieldName = this.fieldResolver.resolveField(address.name, offset);
      if (fieldName) {
        return `${address.name}->${fieldName}`;
      }
    }
    return null;
  }

  private formatExpression(expr: Expression, parentPrec: number): string {
    switch (expr.kind) {
      case 'var':
        return this.zeroInitLocals.has(expr.name) ? formatConstant(0, expr.type) : expr.name;
      case 'const':
        return formatConstant(expr.value, expr.type);
      case 'string_literal':
        return expr.value;
      case 'global':
        return expr.name;
      case 'binary':
        return this.formatBinary(expr, parentPrec);
      case 'compare':
        return this.formatPossiblyUnsigned(expr.op, expr.left, expr.right, PRECEDENCE[expr.op] || 8, parentPrec);
      case 'unary':
        return this.formatUnary(expr, parentPrec);
      case 'load':
        return this.formatLoad(expr);
      case 'call':
        return `${expr.name}(${expr.args.map(arg => this.formatExpression(arg, 0)).join(', ')})`;
      case 'call_indirect':
        return `table[${this.formatExpression(expr.tableIndex, 0)}](${expr.args.map(arg => this.formatExpression(arg, 0)).join(', ')})`;
      case 'select':
        return maybeWrap(
          `${this.formatExpression(expr.condition, PRECEDENCE['?:'])} ? ${this.formatExpression(expr.trueVal, PRECEDENCE['?:'])} : ${this.formatExpression(expr.falseVal, PRECEDENCE['?:'])}`,
          PRECEDENCE['?:'], parentPrec,
        );
      case 'convert':
        return this.formatConvert(expr);
      case 'field_access':
        return this.formatFieldAccess(expr.base, expr.offset);
      case 'frame_slot':
        return this.formatFrameSlot(expr);
      default:
        return `/* unknown expr: ${(expr as Expression).kind} */`;
    }
  }

  // The address of a frame slot: `&frame[k]`. The buffer is a byte array, so the
  // subscript is the raw byte offset (the load/store width casts the slot pointer).
  private formatFrameSlot(expr: Expression & { kind: 'frame_slot' }): string {
    const bufferName = this.frameBuffer ? this.frameBuffer.name : 'frame';
    return `&${bufferName}[${this.formatExpression(expr.index, 0)}]`;
  }

  private formatBinary(expr: Expression & { kind: 'binary' }, parentPrec: number): string {
    if (expr.op === '[]') {
      return `${this.formatExpression(expr.left, FORCE_PARENS)}[${this.formatExpression(expr.right, 0)}]`;
    }
    // Strength reduction: x << N → x * 2^N for readability.
    if (expr.op === '<<' && expr.right.kind === 'const' && typeof expr.right.value === 'number' && expr.right.value >= 1 && expr.right.value <= 3) {
      const prec = PRECEDENCE['*'];
      return maybeWrap(`${this.formatExpression(expr.left, prec)} * ${1 << expr.right.value}`, prec, parentPrec);
    }
    if (isIntrinsicOp(expr.op) || CALL_STYLE_BINARY_OPS.has(expr.op)) {
      return `${expr.op}(${this.formatExpression(expr.left, 0)}, ${this.formatExpression(expr.right, 0)})`;
    }
    // Unsigned (logical) right shift: cast the value, not the shift amount.
    if (expr.op === '>>>') {
      const left = `(unsigned)${this.formatExpression(expr.left, PRECEDENCE['unary'])}`;
      return maybeWrap(`${left} >> ${this.formatExpression(expr.right, PRECEDENCE['>>'] + 1)}`, PRECEDENCE['>>'], parentPrec);
    }
    return this.formatPossiblyUnsigned(expr.op, expr.left, expr.right, PRECEDENCE[expr.op] || 10, parentPrec);
  }

  // Emits `a op b`, or `(unsigned)a op (unsigned)b` for the unsigned operators.
  private formatPossiblyUnsigned(op: string, leftExpr: Expression, rightExpr: Expression, prec: number, parentPrec: number): string {
    const unsignedOp = UNSIGNED_OPS[op];
    if (unsignedOp) {
      const left = `(unsigned)${this.formatExpression(leftExpr, PRECEDENCE['unary'])}`;
      const right = `(unsigned)${this.formatExpression(rightExpr, PRECEDENCE['unary'])}`;
      return maybeWrap(`${left} ${unsignedOp} ${right}`, prec, parentPrec);
    }
    const left = this.formatExpression(leftExpr, prec);
    const right = this.formatExpression(rightExpr, prec + 1);
    return maybeWrap(`${left} ${op} ${right}`, prec, parentPrec);
  }

  private formatUnary(expr: Expression & { kind: 'unary' }, parentPrec: number): string {
    const operand = this.formatExpression(expr.operand, PRECEDENCE['unary']);
    if (expr.op === '!') {
      return maybeWrap(`!${operand}`, PRECEDENCE['unary'], parentPrec);
    }
    if (expr.op === 'neg' || expr.op === '-') {
      return maybeWrap(`-${operand}`, PRECEDENCE['unary'], parentPrec);
    }
    return `${expr.op}(${this.formatExpression(expr.operand, 0)})`;
  }

  private formatLoad(expr: Expression & { kind: 'load' }): string {
    if (expr.address.kind === 'frame_slot') {
      const slot = this.formatFrameSlot(expr.address);
      if (expr.loadType.includes('v128') || expr.loadType.includes('atomic')) {
        return `${expr.loadType.replace(/\./g, '_')}(${slot})`;
      }
      return `*(${accessCType(expr.loadType)}*)${slot}`;
    }
    if (expr.address.kind === 'field_access') {
      const fieldAccess = this.formatFieldAccess(expr.address.base, expr.address.offset);
      const cast = LOAD_TYPE_CAST[expr.loadType];
      return cast ? `(${cast})${fieldAccess}` : fieldAccess;
    }
    if (!expr.loadType.includes('v128') && !expr.loadType.includes('atomic')) {
      const namedField = this.namedFieldAccess(expr.address, expr.offset);
      if (namedField) {
        const cast = LOAD_TYPE_CAST[expr.loadType];
        return cast ? `(${cast})${namedField}` : namedField;
      }
    }
    const target = memoryIndex(this.formatExpression(expr.address, 0), expr.offset);
    if (expr.loadType.includes('v128') || expr.loadType.includes('atomic')) {
      return `${expr.loadType.replace(/\./g, '_')}(${target})`;
    }
    const cast = LOAD_TYPE_CAST[expr.loadType];
    return cast ? `(${cast})memory[${target}]` : `memory[${target}]`;
  }

  private formatConvert(expr: Expression & { kind: 'convert' }): string {
    if (expr.op.startsWith('(')) {
      return `${expr.op}${this.formatExpression(expr.operand, PRECEDENCE['unary'])}`;
    }
    return `${expr.op}(${this.formatExpression(expr.operand, 0)})`;
  }

  private formatStatementInline(statement: Statement): string {
    if (statement.kind === 'assign') {
      return `${statement.target} = ${this.formatExpression(statement.value, 0)}`;
    }
    return '';
  }

  // A multi-value return lists every value, matching the comma-separated return
  // type in the signature; it never collapses to a single value.
  private formatReturn(values: Expression[]): string {
    if (values.length === 0) {
      return 'return;';
    }
    return `return ${values.map(value => this.formatExpression(value, 0)).join(', ')};`;
  }
}

function isEmptyLowered(node: LoweredNode): boolean {
  if (node.kind === 'sequence' && node.children.length === 0) { return true; }
  if (node.kind === 'block' && node.body.length === 0) { return true; }
  return false;
}

export function collectAssignTargets(node: LoweredNode, targets: Set<string>): void {
  switch (node.kind) {
    case 'sequence':
      for (const child of node.children) { collectAssignTargets(child, targets); }
      break;
    case 'block':
      for (const statement of node.body) {
        if (statement.kind === 'assign') { targets.add(statement.target); }
        if (statement.kind === 'call' && statement.result) { targets.add(statement.result); }
        if (statement.kind === 'call_indirect' && statement.result) { targets.add(statement.result); }
      }
      break;
    case 'if':
      collectAssignTargets(node.thenBody, targets);
      if (node.elseBody) { collectAssignTargets(node.elseBody, targets); }
      break;
    case 'while':
      collectAssignTargets(node.body, targets);
      break;
    case 'do_while':
      collectAssignTargets(node.body, targets);
      break;
    case 'labeled_block':
      collectAssignTargets(node.body, targets);
      break;
    case 'for':
      if (node.init.kind === 'assign') { targets.add(node.init.target); }
      if (node.increment.kind === 'assign') { targets.add(node.increment.target); }
      collectAssignTargets(node.body, targets);
      break;
    case 'switch':
      for (const caseEntry of node.cases) { collectAssignTargets(caseEntry.body, targets); }
      collectAssignTargets(node.defaultBody, targets);
      break;
  }
}

function collectAssignTypes(node: LoweredNode, types: Map<string, string>): void {
  switch (node.kind) {
    case 'sequence':
      for (const child of node.children) { collectAssignTypes(child, types); }
      break;
    case 'block':
      for (const statement of node.body) {
        if (statement.kind === 'assign' && !types.has(statement.target)) { types.set(statement.target, statement.type); }
      }
      break;
    case 'if':
      collectAssignTypes(node.thenBody, types);
      if (node.elseBody) { collectAssignTypes(node.elseBody, types); }
      break;
    case 'while': collectAssignTypes(node.body, types); break;
    case 'do_while': collectAssignTypes(node.body, types); break;
    case 'for':
      if (node.init.kind === 'assign' && !types.has(node.init.target)) { types.set(node.init.target, node.init.type); }
      if (node.increment.kind === 'assign' && !types.has(node.increment.target)) { types.set(node.increment.target, node.increment.type); }
      collectAssignTypes(node.body, types);
      break;
    case 'labeled_block': collectAssignTypes(node.body, types); break;
    case 'switch':
      for (const caseEntry of node.cases) { collectAssignTypes(caseEntry.body, types); }
      collectAssignTypes(node.defaultBody, types);
      break;
  }
}

export function collectReferencedVars(node: LoweredNode, refs: Set<string>): void {
  function scanExpr(expr: Expression): void {
    if (expr.kind === 'var') { refs.add(expr.name); }
    if (expr.kind === 'binary') { scanExpr(expr.left); scanExpr(expr.right); }
    if (expr.kind === 'unary') { scanExpr(expr.operand); }
    if (expr.kind === 'compare') { scanExpr(expr.left); scanExpr(expr.right); }
    if (expr.kind === 'load') { scanExpr(expr.address); }
    if (expr.kind === 'call') { for (const arg of expr.args) { scanExpr(arg); } }
    if (expr.kind === 'call_indirect') { scanExpr(expr.tableIndex); for (const arg of expr.args) { scanExpr(arg); } }
    if (expr.kind === 'select') { scanExpr(expr.condition); scanExpr(expr.trueVal); scanExpr(expr.falseVal); }
    if (expr.kind === 'convert') { scanExpr(expr.operand); }
    if (expr.kind === 'field_access') { scanExpr(expr.base); }
    if (expr.kind === 'frame_slot') { scanExpr(expr.index); }
  }
  function scanStmt(statement: Statement): void {
    if (statement.kind === 'assign') { scanExpr(statement.value); }
    if (statement.kind === 'store') { scanExpr(statement.address); scanExpr(statement.value); }
    if (statement.kind === 'call') { for (const arg of statement.args) { scanExpr(arg); } }
    if (statement.kind === 'call_indirect') { scanExpr(statement.tableIndex); for (const arg of statement.args) { scanExpr(arg); } }
    if (statement.kind === 'global_set') { scanExpr(statement.value); }
    if (statement.kind === 'return') { for (const value of statement.values) { scanExpr(value); } }
  }
  switch (node.kind) {
    case 'sequence': for (const child of node.children) { collectReferencedVars(child, refs); } break;
    case 'block': for (const statement of node.body) { scanStmt(statement); } break;
    case 'if': if (node.condition) { scanExpr(node.condition); } collectReferencedVars(node.thenBody, refs); if (node.elseBody) { collectReferencedVars(node.elseBody, refs); } break;
    case 'while': if (node.condition) { scanExpr(node.condition); } collectReferencedVars(node.body, refs); break;
    case 'do_while': collectReferencedVars(node.body, refs); scanExpr(node.condition); break;
    case 'for': scanStmt(node.init); scanExpr(node.condition); scanStmt(node.increment); collectReferencedVars(node.body, refs); break;
    case 'labeled_block': collectReferencedVars(node.body, refs); break;
    case 'switch': scanExpr(node.selector); for (const caseEntry of node.cases) { collectReferencedVars(caseEntry.body, refs); } collectReferencedVars(node.defaultBody, refs); break;
    case 'return': for (const value of node.values) { scanExpr(value); } break;
  }
}

function alwaysTerminates(node: LoweredNode): boolean {
  switch (node.kind) {
    case 'return':
    case 'break':
    case 'continue':
    case 'unreachable':
    case 'labeled_break':
    case 'labeled_continue':
      return true;
    case 'sequence':
      return node.children.length > 0 && alwaysTerminates(node.children[node.children.length - 1]);
    case 'block':
      return node.body.length > 0 && node.body[node.body.length - 1].kind === 'return';
    case 'if':
      return node.elseBody !== null && alwaysTerminates(node.thenBody) && alwaysTerminates(node.elseBody);
    default:
      return false;
  }
}
